import React from "react";
import "./Chatbot.css";

const FALLBACK_IMAGES = {
  sikkim: "https://images.unsplash.com/photo-1544735716-392fe2489ffa?w=600&auto=format&fit=crop&q=80",
  gangtok: "https://images.unsplash.com/photo-1589182373726-e4f658ab50f0?w=600&auto=format&fit=crop&q=80",
  darjeeling: "https://images.unsplash.com/photo-1589182373726-e4f658ab50f0?w=600&auto=format&fit=crop&q=80",
  kalimpong: "https://images.unsplash.com/photo-1544735716-392fe2489ffa?w=600&auto=format&fit=crop&q=80",
  manali: "https://images.unsplash.com/photo-1626621341517-bbf3d9990a23?w=600&auto=format&fit=crop&q=80",
  himachal: "https://images.unsplash.com/photo-1626621341517-bbf3d9990a23?w=600&auto=format&fit=crop&q=80",
  spiti: "https://images.unsplash.com/photo-1605649487212-47bdab064df7?w=600&auto=format&fit=crop&q=80",
  ladakh: "https://images.unsplash.com/photo-1605649487212-47bdab064df7?w=600&auto=format&fit=crop&q=80",
  kashmir: "https://images.unsplash.com/photo-1595815771614-ade9d652a65d?w=600&auto=format&fit=crop&q=80",
  default_tour: "https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?w=600&auto=format&fit=crop&q=80",
  default_stay: "https://images.unsplash.com/photo-1587061949409-02df41d5e562?w=600&auto=format&fit=crop&q=80",
  default_cab: "https://images.unsplash.com/photo-1533473359331-0135ef1b58bf?w=600&auto=format&fit=crop&q=80",
  default_sight: "https://images.unsplash.com/photo-1506744038136-46273834b3fb?w=600&auto=format&fit=crop&q=80",
};

function getCardImage(item, defaultType = "default_tour") {
  if (item && item.image && typeof item.image === "string" && item.image.startsWith("http")) {
    return item.image;
  }
  const text = `${item?.title || ""} ${item?.name || ""} ${item?.location || ""} ${item?.city || ""}`.toLowerCase();
  for (const [key, url] of Object.entries(FALLBACK_IMAGES)) {
    if (text.includes(key)) return url;
  }
  return FALLBACK_IMAGES[defaultType] || FALLBACK_IMAGES.default_tour;
}

// Wellness packages use a different API than tours; infer from the card item / URL
function getPreviewType(item, url) {
  const t = String(item?.package_type || item?.kind || item?.type || "").toLowerCase();
  return t === "wellness" || /\/wellness/i.test(url || "") ? "wellness" : "tour";
}

function formatPriceDisplay(price, fallback = "₹1,500") {
  if (typeof price === "number" && !isNaN(price)) {
    return `₹${price.toLocaleString("en-IN")}`;
  }
  if (typeof price === "string" && price.trim()) {
    if (price.toLowerCase().includes("request")) return fallback;
    if (price.startsWith("₹")) return price;
    const num = Number(price.replace(/[^0-9]/g, ""));
    if (!isNaN(num) && num > 0) return `₹${num.toLocaleString("en-IN")}`;
    return price;
  }
  return fallback;
}

/**
 * Universal Card component that enforces 100% IDENTICAL design language
 * across all generative UI categories (packages, stays, cabs, sights, activities).
 */
function UnifiedGenerativeCard({
  badge,
  badgeType = "green",
  seatsBadge,
  isUrgent,
  image,
  fallbackType,
  subLabel,
  title,
  meta = [],
  price,
  priceUnit,
  targetUrl,
  targetTitle,
  previewId,
  previewType,
  onOpenPreview,
}) {
  const cardImg = image || getCardImage({ title }, fallbackType);

  const handleOpenSidebar = () => {
    if (onOpenPreview && targetUrl) {
      onOpenPreview({
        id: previewId,
        type: previewType,
        title: targetTitle || title,
        url: targetUrl,
      });
    }
  };

  return (
    <div className="tgh-gen-card" onClick={handleOpenSidebar}>
      {/* 1. Uniform Hero Media */}
      <div className="tgh-gen-card-media">
        <img
          src={cardImg}
          alt={title}
          className="tgh-gen-card-img"
          loading="lazy"
          onError={(e) => {
            e.currentTarget.src = FALLBACK_IMAGES[fallbackType] || FALLBACK_IMAGES.default_tour;
          }}
        />
        <div className="tgh-gen-media-scrim" />
        <div className="tgh-gen-media-top">
          <span className={`tgh-gen-badge tgh-gen-badge--${badgeType}`}>
            {badge}
          </span>
          {seatsBadge && (
            <span className={`tgh-gen-seats-badge ${isUrgent ? "tgh-gen-seats-badge--urgent" : ""}`}>
              {seatsBadge}
            </span>
          )}
        </div>
      </div>

      {/* 2. Uniform Card Body */}
      <div className="tgh-gen-card-body">
        <div>
          {subLabel && <span className="tgh-gen-card-sublabel">{subLabel}</span>}
          <h5 className="tgh-gen-card-title" title={title}>
            {title}
          </h5>

          {/* Meta Items with identical icons */}
          <div className="tgh-gen-card-meta">
            {meta.map((m, i) => (
              m.text ? (
                <span key={i} className={`tgh-gen-meta-item ${m.highlight ? "tgh-gen-meta-item--highlight" : ""}`}>
                  {m.icon} {m.text}
                </span>
              ) : null
            ))}
          </div>
        </div>

        {/* 3. Uniform Pricing & Action */}
        <div>
          <div className="tgh-gen-card-bottom">
            <span className="tgh-gen-price">{price}</span>
            {priceUnit && <span className="tgh-gen-price-sub">{priceUnit}</span>}
          </div>

          <button
            type="button"
            className="tgh-gen-btn--sidebar"
            onClick={(e) => {
              e.stopPropagation();
              handleOpenSidebar();
            }}
            title="Open direct live page in sidebar"
          >
            Open in Sidebar
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
              <line x1="15" y1="3" x2="15" y2="21" />
              <polyline points="9 10 12 12 9 14" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Master Generative UI Component with strictly identical design language.
 */
export default function GenerativeCards({ cards, onOpenPreview, onAskAbout }) {
  if (!cards) return null;

  // 1. Confirmed Upcoming Departures (Next 30 Days)
  if (cards.type === "upcoming_packages" && Array.isArray(cards.items)) {
    return (
      <div className="tgh-gen-container">
        <div className="tgh-gen-header">
          <span className="tgh-gen-header-pill">Live Departures</span>
          <h4 className="tgh-gen-header-title">{cards.title || "Next 30 Days Confirmed Departures"}</h4>
        </div>
        <div className="tgh-gen-scroll-row">
          {cards.items.map((item, idx) => {
            const tourId = item.id || item.tour_id;
            const targetUrl = item.url && item.url.includes("/tours/")
              ? item.url
              : (tourId ? `/tours/${tourId}` : "/packages");

            const seatsAvailable = item.seats_available;
            const isLow = seatsAvailable !== undefined && seatsAvailable <= 5;
            const seatsText = seatsAvailable !== undefined ? `${isLow ? "🔥 " : ""}${seatsAvailable} seats left` : null;

            return (
              <UnifiedGenerativeCard
                key={idx}
                badge={item.badge || "Confirmed"}
                badgeType="green"
                seatsBadge={seatsText}
                isUrgent={isLow}
                image={item.image}
                fallbackType="default_tour"
                subLabel={item.location ? `${item.location} • GROUP DEPARTURE` : "GROUP DEPARTURE"}
                title={item.title}
                meta={[
                  { icon: "📍", text: item.location },
                  { icon: "⏱️", text: item.duration },
                  { icon: "📅", text: item.departure_date ? `Departs ${item.departure_date}` : null, highlight: true }
                ]}
                price={formatPriceDisplay(item.price, "₹18,500")}
                priceUnit="/ person"
                targetUrl={targetUrl}
                targetTitle={item.title}
                previewId={tourId}
                previewType={getPreviewType(item, targetUrl)}
                onOpenPreview={onOpenPreview}
              />
            );
          })}
        </div>
      </div>
    );
  }

  // 2. Standard Curated Tour Packages
  if (cards.type === "tours" && Array.isArray(cards.items)) {
    return (
      <div className="tgh-gen-container">
        <div className="tgh-gen-header">
          <span className="tgh-gen-header-pill">Curated Tours</span>
          <h4 className="tgh-gen-header-title">{cards.title || "Recommended Mountain Packages"}</h4>
        </div>
        <div className="tgh-gen-scroll-row">
          {cards.items.map((item, idx) => {
            const tourId = item.id || item.tour_id;
            const targetUrl = item.url && item.url.includes("/tours/")
              ? item.url
              : (tourId ? `/tours/${tourId}` : "/packages");

            return (
              <UnifiedGenerativeCard
                key={idx}
                badge={item.badge || "Curated Tour"}
                badgeType="green"
                seatsBadge={item.category || item.theme || "Scenic Tour"}
                isUrgent={false}
                image={item.image}
                fallbackType="default_tour"
                subLabel={item.location ? `${item.location} • HIMALAYAN CIRCUIT` : "HIMALAYAN CIRCUIT"}
                title={item.title}
                meta={[
                  { icon: "📍", text: item.location },
                  { icon: "⏱️", text: item.duration },
                  { icon: "🚩", text: item.start_point ? `Starts: ${item.start_point}` : null }
                ]}
                price={formatPriceDisplay(item.price, "₹15,000")}
                priceUnit="starting"
                targetUrl={targetUrl}
                targetTitle={item.title}
                previewId={tourId}
                previewType={getPreviewType(item, targetUrl)}
                onOpenPreview={onOpenPreview}
              />
            );
          })}
        </div>
      </div>
    );
  }

  // 3. Boutique Stays and Hotels
  if (cards.type === "stays" && Array.isArray(cards.items)) {
    return (
      <div className="tgh-gen-container">
        <div className="tgh-gen-header">
          <span className="tgh-gen-header-pill">Boutique Stays</span>
          <h4 className="tgh-gen-header-title">{cards.title || "Handpicked Mountain Stays"}</h4>
        </div>
        <div className="tgh-gen-scroll-row">
          {cards.items.map((item, idx) => {
            const propId = item.id || item.property_id;
            const targetUrl = item.url && item.url.includes("/property/")
              ? item.url
              : (propId ? `/hotel/property/${propId}` : "/hotels");

            return (
              <UnifiedGenerativeCard
                key={idx}
                badge={item.badge || "Verified Stay"}
                badgeType="green"
                seatsBadge={item.rating ? `⭐ ${item.rating}` : "Verified"}
                isUrgent={false}
                image={item.image}
                fallbackType="default_stay"
                subLabel={item.city ? `${item.city} • BOUTIQUE STAY` : "HIMALAYAN STAY"}
                title={item.name}
                meta={[
                  { icon: "📍", text: item.city },
                  { icon: "🏡", text: item.type || "Mountain Retreat" },
                  { icon: "✨", text: item.amenities && item.amenities[0] ? item.amenities[0] : "Verified Amenities" }
                ]}
                price={formatPriceDisplay(item.price, "₹1,800")}
                priceUnit="/ night"
                targetUrl={targetUrl}
                targetTitle={item.name}
                onOpenPreview={onOpenPreview}
              />
            );
          })}
        </div>
      </div>
    );
  }

  // 4. Mountain Cabs and Transfers
  if (cards.type === "cabs" && Array.isArray(cards.items)) {
    return (
      <div className="tgh-gen-container">
        <div className="tgh-gen-header">
          <span className="tgh-gen-header-pill">Mountain Cabs</span>
          <h4 className="tgh-gen-header-title">{cards.title || "Available Mountain Cabs"}</h4>
        </div>
        <div className="tgh-gen-scroll-row">
          {cards.items.map((item, idx) => {
            const targetUrl = item.url || "/bookCabs";
            const priceText = item.shared_fare || item.full_cab_fare || "₹2,500";

            return (
              <UnifiedGenerativeCard
                key={idx}
                badge="Mountain Transfer"
                badgeType="amber"
                seatsBadge={item.capacity ? `🚗 ${item.capacity} Seats` : "Verified SUV"}
                isUrgent={false}
                image={item.image}
                fallbackType="default_cab"
                subLabel="VERIFIED HIMALAYAN RIDE"
                title={item.name || `${item.pickup || "Pickup"} to ${item.drop || "Drop"}`}
                meta={[
                  { icon: "📍", text: item.distance ? `Distance: ${item.distance}` : null },
                  { icon: "⏱️", text: item.time ? `Est. Time: ${item.time}` : null },
                  { icon: "🚗", text: item.vehicle_type || "Commercial Himalayan Vehicle" }
                ]}
                price={priceText}
                priceUnit=""
                targetUrl={targetUrl}
                targetTitle={item.name || "TGH Cab Booking"}
                onOpenPreview={onOpenPreview}
              />
            );
          })}
        </div>
      </div>
    );
  }

  // 5. Destination Ecosystem (Sightseeing + Activities)
  if (cards.type === "destination_ecosystem" || cards.type === "sightseeing_activities") {
    const sightseeing = cards.sightseeing || cards.sightseeing_places || [];
    const activities = cards.activities || [];
    const dest = cards.destination || cards.location || "Himalayan";

    return (
      <div className="tgh-gen-container">
        <div className="tgh-gen-header">
          <span className="tgh-gen-header-pill">Destination Guide</span>
          <h4 className="tgh-gen-header-title">{cards.title || `${dest} Highlights & Sights`}</h4>
        </div>
        <div className="tgh-gen-scroll-row">
          {sightseeing.map((s, idx) => (
            <UnifiedGenerativeCard
              key={`s-${idx}`}
              badge={s.category || "Sightseeing"}
              badgeType="amber"
              seatsBadge={s.rating ? `⭐ ${s.rating}` : "Must Visit"}
              isUrgent={false}
              image={s.image}
              fallbackType="default_sight"
              subLabel={`${dest} • ATTRACTION`}
              title={s.name}
              meta={[
                { icon: "📍", text: s.location || dest },
                { icon: "🕒", text: s.best_time ? `Best: ${s.best_time}` : "Open Daily" }
              ]}
              price="Scenic Spot"
              priceUnit="entry included"
              targetUrl={`/packages`}
              targetTitle={s.name}
              onOpenPreview={onOpenPreview}
            />
          ))}

          {activities.map((a, idx) => (
            <UnifiedGenerativeCard
              key={`a-${idx}`}
              badge="Adventure"
              badgeType="blue"
              seatsBadge={a.difficulty || "All Levels"}
              isUrgent={false}
              image={a.image}
              fallbackType="default_sight"
              subLabel={`${dest} • ACTIVITY`}
              title={a.name}
              meta={[
                { icon: "⏱️", text: a.duration || "2-3 Hours" },
                { icon: "⚡", text: a.difficulty ? `Level: ${a.difficulty}` : "Guided Experience" }
              ]}
              price={formatPriceDisplay(a.price, "₹1,200")}
              priceUnit="/ person"
              targetUrl={`/packages`}
              targetTitle={a.name}
              onOpenPreview={onOpenPreview}
            />
          ))}
        </div>
      </div>
    );
  }

  return null;
}